/**
 * Background photo upload queue.
 *
 * Every photo that leaves the camera — a snapped frame or files picked from the
 * photo library — is handed to this module. It persists the raw blob in
 * IndexedDB and uploads it from a single worker that lives for the whole app
 * session, not inside the component that created it. Uploads therefore keep
 * going when the camera closes, when the user navigates to another project,
 * and (because the queue is persisted) when the PWA is killed and reopened.
 *
 * Why IndexedDB rather than the older localStorage queue in photoService:
 * localStorage holds ~5MB of base64 on iOS, which a single HEIC can exhaust.
 * IndexedDB stores the Blob as-is and has a quota measured in hundreds of MB.
 *
 * Only one tab should run this (two tabs would upload the same jobs twice); on
 * the phones this app targets that is always the case.
 */
import { v4 as uuidv4 } from 'uuid';
import { uploadPhoto } from './photoService';

export interface UploadTarget {
  projectId: string;
  roomId?: string;
  itemId?: string;
}

export interface UploadJobListeners {
  /** The photo document exists and both renditions are in Storage. */
  onUploaded?: (jobId: string, photoId: string) => void;
  /** The job gave up after MAX_ATTEMPTS. It stays queued for a manual retry. */
  onFailed?: (jobId: string, error: unknown) => void;
}

export interface UploadQueueSnapshot {
  /** Jobs waiting or in flight. */
  pendingCount: number;
  /** Jobs that gave up and wait for the user to retry or discard them. */
  failedCount: number;
  /** Uploads finished since the queue was last idle — drives "3 av 12". */
  completedInBatch: number;
}

type JobStatus = 'queued' | 'uploading' | 'failed';

interface StoredJob extends UploadTarget {
  id: string;
  blob: Blob;
  createdAt: number;
}

interface Job extends StoredJob {
  status: JobStatus;
  attempts: number;
  /** Resolves once the job is in IndexedDB (or persistence gave up). */
  persisted: Promise<void>;
}

const DB_NAME = 'pickup-upload-queue';
const DB_VERSION = 1;
const STORE = 'jobs';
const MAX_ATTEMPTS = 3;
const RETRY_DELAYS_MS = [3000, 8000];

// ---------------------------------------------------------------------------
// IndexedDB — raw API; small enough not to warrant a dependency.
// ---------------------------------------------------------------------------

const request = <T>(req: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

let dbPromise: Promise<IDBDatabase> | null = null;

const openDb = (): Promise<IDBDatabase> => {
  if (!dbPromise) {
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        reject(new Error('IndexedDB is not available'));
        return;
      }
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE)) {
          req.result.createObjectStore(STORE, { keyPath: 'id' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
      req.onblocked = () => reject(new Error('IndexedDB open was blocked'));
    });
    // A failed open (private mode, quota) should not poison every later call.
    dbPromise.catch(() => {
      dbPromise = null;
    });
  }
  return dbPromise;
};

const idbPut = async (job: StoredJob): Promise<void> => {
  const db = await openDb();
  await request(db.transaction(STORE, 'readwrite').objectStore(STORE).put(job));
};

const idbDelete = async (id: string): Promise<void> => {
  const db = await openDb();
  await request(db.transaction(STORE, 'readwrite').objectStore(STORE).delete(id));
};

const idbGetAll = async (): Promise<StoredJob[]> => {
  const db = await openDb();
  return request(db.transaction(STORE, 'readonly').objectStore(STORE).getAll());
};

// ---------------------------------------------------------------------------
// In-memory state + subscriptions (consumed via useSyncExternalStore)
// ---------------------------------------------------------------------------

let jobs: Job[] = [];
const listeners = new Map<string, UploadJobListeners>();
const subscribers = new Set<() => void>();
let completedInBatch = 0;
let workerRunning = false;
let started = false;
let snapshot: UploadQueueSnapshot = { pendingCount: 0, failedCount: 0, completedInBatch: 0 };

const emit = () => {
  snapshot = {
    pendingCount: jobs.filter(j => j.status !== 'failed').length,
    failedCount: jobs.filter(j => j.status === 'failed').length,
    completedInBatch,
  };
  subscribers.forEach(fn => fn());
};

export const getUploadQueueSnapshot = (): UploadQueueSnapshot => snapshot;

export const subscribeUploadQueue = (fn: () => void): (() => void) => {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
};

const sleep = (ms: number) => new Promise<void>(resolve => window.setTimeout(resolve, ms));

const waitForOnline = () =>
  new Promise<void>(resolve => {
    if (navigator.onLine) {
      resolve();
      return;
    }
    const onOnline = () => {
      window.removeEventListener('online', onOnline);
      resolve();
    };
    window.addEventListener('online', onOnline);
  });

const takeListeners = (jobId: string): UploadJobListeners | undefined => {
  const l = listeners.get(jobId);
  listeners.delete(jobId);
  return l;
};

// ---------------------------------------------------------------------------
// Worker — one job at a time, in the order they were queued.
// ---------------------------------------------------------------------------

const runWorker = async (): Promise<void> => {
  if (workerRunning) return;
  workerRunning = true;
  try {
    for (;;) {
      const job = jobs.find(j => j.status === 'queued');
      if (!job) break;

      if (!navigator.onLine) {
        // Nothing to do until the signal is back. Jobs stay persisted meanwhile.
        await waitForOnline();
        continue;
      }

      job.status = 'uploading';
      emit();

      try {
        const photo = await uploadPhoto(job.projectId, job.blob, {
          roomId: job.roomId,
          itemId: job.itemId,
        });
        // Never delete before the put has landed, or a slow put would resurrect
        // an already-uploaded job on the next launch.
        await job.persisted;
        jobs = jobs.filter(j => j.id !== job.id);
        completedInBatch++;
        emit();
        idbDelete(job.id).catch(err => console.warn('Could not remove uploaded job from IndexedDB:', err));
        takeListeners(job.id)?.onUploaded?.(job.id, photo.id);
      } catch (err) {
        job.attempts++;
        if (job.attempts < MAX_ATTEMPTS) {
          console.warn(`Photo upload attempt ${job.attempts} failed, retrying:`, err);
          job.status = 'queued';
          emit();
          await sleep(RETRY_DELAYS_MS[Math.min(job.attempts - 1, RETRY_DELAYS_MS.length - 1)]);
        } else {
          console.error('Photo upload gave up after repeated failures:', err);
          job.status = 'failed';
          emit();
          takeListeners(job.id)?.onFailed?.(job.id, err);
        }
      }
    }
  } finally {
    workerRunning = false;
  }
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

const toJob = (stored: StoredJob, persisted: Promise<void>): Job => ({
  ...stored,
  status: 'queued',
  attempts: 0,
  persisted,
});

/**
 * Queue photos for upload and return immediately. Returned job ids are in the
 * same order as `blobs`, so callers can map them back to their own previews.
 * Listeners are held in memory only: after a reload the jobs still upload, but
 * nobody is called back (pages refetch on mount anyway).
 */
export const enqueuePhotoUploads = (
  blobs: Blob[],
  target: UploadTarget,
  jobListeners?: UploadJobListeners
): string[] => {
  if (blobs.length === 0) return [];
  if (snapshot.pendingCount === 0) completedInBatch = 0; // a fresh batch

  const now = Date.now();
  const created = blobs.map((blob, i) => {
    const stored: StoredJob = { id: uuidv4(), projectId: target.projectId, blob, createdAt: now + i };
    if (target.roomId) stored.roomId = target.roomId;
    if (target.itemId) stored.itemId = target.itemId;
    const persisted = idbPut(stored).catch(err => {
      console.warn('Could not persist upload job; it will still upload this session:', err);
    });
    return toJob(stored, persisted);
  });

  jobs.push(...created);
  if (jobListeners) created.forEach(j => listeners.set(j.id, jobListeners));
  emit();
  void runWorker();
  return created.map(j => j.id);
};

export const retryFailedUploads = (): void => {
  let changed = false;
  jobs.forEach(j => {
    if (j.status === 'failed') {
      j.status = 'queued';
      j.attempts = 0;
      changed = true;
    }
  });
  if (!changed) return;
  emit();
  void runWorker();
};

export const discardFailedUploads = (): void => {
  const failed = jobs.filter(j => j.status === 'failed');
  if (failed.length === 0) return;
  jobs = jobs.filter(j => j.status !== 'failed');
  failed.forEach(j => {
    listeners.delete(j.id);
    j.persisted
      .then(() => idbDelete(j.id))
      .catch(err => console.warn('Could not remove discarded job from IndexedDB:', err));
  });
  emit();
};

/**
 * Start the worker: reload jobs persisted by an earlier session, then keep
 * draining whenever connectivity returns or the app comes back to the
 * foreground. Call once, after sign-in (uploads need an authenticated user).
 */
export const startUploadQueue = async (): Promise<void> => {
  if (started) return;
  started = true;

  try {
    const stored = await idbGetAll();
    const known = new Set(jobs.map(j => j.id));
    const restored = stored
      .filter(s => !known.has(s.id))
      .sort((a, b) => a.createdAt - b.createdAt)
      .map(s => toJob(s, Promise.resolve()));
    if (restored.length > 0) {
      console.log(`📤 Resuming ${restored.length} photo upload(s) from a previous session`);
      // Older than anything enqueued while this read was in flight, so first.
      jobs = [...restored, ...jobs];
      emit();
    }
  } catch (err) {
    console.warn('Could not read the persisted upload queue:', err);
  }

  window.addEventListener('online', () => {
    // A lost signal is the usual reason a job gave up; give it another go.
    retryFailedUploads();
    void runWorker();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void runWorker();
  });
  void runWorker();
};

// ---------------------------------------------------------------------------
// Copy helpers — pure, so the Swedish pluralisation is unit-testable.
// ---------------------------------------------------------------------------

/** Status line for the progress pill, or null when there is nothing to say. */
export const describeUploadQueue = (s: UploadQueueSnapshot): string | null => {
  if (s.pendingCount > 0) {
    const total = s.completedInBatch + s.pendingCount;
    return total === 1
      ? 'Laddar upp 1 foto…'
      : `Laddar upp ${s.completedInBatch + 1} av ${total} foton…`;
  }
  if (s.failedCount > 0) {
    return s.failedCount === 1
      ? '1 foto kunde inte laddas upp'
      : `${s.failedCount} foton kunde inte laddas upp`;
  }
  return null;
};

export const uploadedLabel = (count: number): string =>
  count === 1 ? '1 foto uppladdat' : `${count} foton uppladdade`;
